import { IMediaItem } from "../../data/AppInterface";
import {
  TBitValue,
  TReactSetState,
  TUseTranslationTfn,
} from "../../data/AppType";
import {
  CREATE_HELP_TOPIC,
  GET_HELP_TOPIC_BY_ID,
  UPDATE_HELP_TOPIC,
} from "../../services/Endpoints";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import { BIT_VALUE, UPLOAD_DOCUMENT_TYPE } from "../../utils/AppEnumerations";
import {
  handleError,
  prepareMessageFromParams,
  sweetAlertError,
  sweetAlertSuccess,
} from "../../utils/AppFunctions";
import * as Yup from "yup";

export interface IHelpTopicDocument {
  id: string;
  document_url: string;
  document_type: UPLOAD_DOCUMENT_TYPE;
}

export interface IHelpTopicForm {
  id?: string;
  topic_title: string;
  topic_description: string;
  help_category_id?: string;
  display_sequence?: number;
  tags: string[] | null;
  reference_link: string | null;
  is_active: TBitValue;
  video_url: string | null;
  image_url: string | null;
  pdf_url: string | null;
  created_by?: string;
  created_date?: string;
  modified_by?: string;
  modified_date?: string;
}

export const HELP_TOPIC_FIELD_INITIAL_VALUES = {
  topic_title: "",
  topic_description: "",
  help_category_id: undefined,
  display_sequence: undefined,
  tags: null,
  reference_link: null,
  is_active: BIT_VALUE.One,
  video_url: null,
  image_url: null,
  pdf_url: null,
};

export const helpTopicsFieldValidationSchema = (t: TUseTranslationTfn) =>
  Yup.object().shape({
    topic_title: Yup.string()
      .required(
        prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("topic_title")],
        ])
      )
      .min(
        2,
        prepareMessageFromParams(t("error_message_min_length"), [
          ["fieldName", t("topic_title")],
          ["min", "2"],
        ])
      )
      .max(
        200,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("topic_title")],
          ["max", "200"],
        ])
      ),

    topic_description: Yup.string()
      .required(
        prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("topic_description")],
        ])
      )
      .min(
        2,
        prepareMessageFromParams(t("error_message_min_length"), [
          ["fieldName", t("topic_description")],
          ["min", "2"],
        ])
      )
      .max(
        10000,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("topic_description")],
          ["max", "10000"],
        ])
      ),

    help_category_id: Yup.string().required(
      prepareMessageFromParams(t("error_message_required"), [
        ["fieldName", t("help_category")],
      ])
    ),

    display_sequence: Yup.number()
      .typeError(t("error_message_number"))
      .required(
        prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("display_sequence")],
        ])
      )
      .min(
        1,
        prepareMessageFromParams(t("error_message_min"), [
          ["fieldName", t("display_sequence")],
          ["min", "1"],
        ])
      )
      .max(
        500,
        prepareMessageFromParams(t("error_message_max_value"), [
          ["fieldName", t("display_sequence")],
          ["max", "500"],
        ])
      ),

    tags: Yup.array()
      .of(
        Yup.string()
          .min(
            1,
            prepareMessageFromParams(t("error_message_min_length"), [
              ["fieldName", t("tag")],
              ["min", "1"],
            ])
          )
          .max(
            100,
            prepareMessageFromParams(t("error_message_max_length"), [
              ["fieldName", t("tag")],
              ["max", "100"],
            ])
          )
      )
      .nullable(),

    reference_link: Yup.string()
      .nullable()
      .max(
        1024,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("reference_link")],
          ["max", "1024"],
        ])
      ),

    video_url: Yup.string()
      .nullable()
      .max(
        1024,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("video_url")],
          ["max", "1024"],
        ])
      ),

    image_url: Yup.string()
      .nullable()
      .max(
        1024,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("image_url")],
          ["max", "1024"],
        ])
      ),

    pdf_url: Yup.string()
      .nullable()
      .max(
        1024,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("pdf_url")],
          ["max", "1024"],
        ])
      ),
  });

export const getHelpTopicById = async (
  id: string,
  handleClose: (status: boolean) => void,
  setHelpTopic: TReactSetState<IHelpTopicForm | undefined>,
  setUploadedDoc: TReactSetState<IMediaItem[]>,
  setHelpTag: TReactSetState<string[]>
) => {
  try {
    const helpTopicData = await GET_HELP_TOPIC_BY_ID(id);
    if (helpTopicData && helpTopicData.code === DEFAULT_STATUS_CODE_SUCCESS) {
      setHelpTopic({ ...helpTopicData.data, HTD: undefined });
      setHelpTag(
        helpTopicData.data?.tags && helpTopicData.data?.tags.length > 0
          ? helpTopicData.data.tags
          : []
      );
      setUploadedDoc(
        helpTopicData.data?.HTD.map((doc: IHelpTopicDocument) => ({
          ...doc,
          media_url: doc.document_url,
          media_type: doc.document_type,
        }))
      );
    } else {
      handleClose(false);
      sweetAlertError({
        message: helpTopicData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (e: any) {
    handleClose(false);
    handleError(e, "getHelpTopicById");
  }
};

export const handleSubmitHelpTopic = async (
  values: IHelpTopicForm,
  setLoading: TReactSetState<boolean>,
  handleClose: (status: boolean) => void,
  id: string | undefined
) => {
  setLoading(true);
  try {
    let helpCategory;
    if (id) {
      helpCategory = await UPDATE_HELP_TOPIC(values, id);
    } else {
      helpCategory = await CREATE_HELP_TOPIC(values);
    }
    if (helpCategory && helpCategory.code === DEFAULT_STATUS_CODE_SUCCESS) {
      sweetAlertSuccess({
        message: helpCategory.message,
      });
      return handleClose(true);
    } else {
      sweetAlertError({
        message: helpCategory?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (e: any) {
    sweetAlertError(e?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    handleError(e, "handleSubmitHelpTopic");
  } finally {
    setLoading(false);
  }
};
